import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiDollarSign, FiCalendar, FiUser, FiFilter, FiDownload, FiSearch } from 'react-icons/fi';
import axiosConfig from '../../../api/axiosConfig';
import { toast } from 'react-toastify';
import { parseMoneyInput, sanitizeMoneyInput } from '../../../utils/moneyUtils';

export default function AvansPage() {
  const adminId = localStorage.getItem('adminId'); // Admin ID'yi al
  const adminRole = localStorage.getItem('adminRole');
  const isSuperAdmin = adminRole === 'superadmin' || adminId === 'superadmin-001';
  const [avanslar, setAvanslar] = useState([]);
  const [ogretmenler, setOgretmenler] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAvans, setEditingAvans] = useState(null);
  const [filter, setFilter] = useState({
    ogretmen_id: '',
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear()
  });
  const [monthlyStats, setMonthlyStats] = useState({
    toplam_avans: 0,
    avans_sayisi: 0
  });
  const [formData, setFormData] = useState({
    ogretmen_id: '',
    tutar: ''
  });
  
  // Arama state'leri
  const [filterSearchTerm, setFilterSearchTerm] = useState('');
  const [modalSearchTerm, setModalSearchTerm] = useState('');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showModalDropdown, setShowModalDropdown] = useState(false);

  // Öğretmenleri arama terimine göre filtrele
  const getFilteredOgretmenler = (searchTerm) => {
    if (!searchTerm) return ogretmenler;
    return ogretmenler.filter(ogretmen => 
      ogretmen.adi.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const fetchAvanslar = async () => {
    try {
      setLoading(true);
      const response = await axiosConfig.get('/admin/avans');
      setAvanslar(response.data);
    } catch (error) {
      toast.error('Avanslar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const fetchOgretmenler = async () => {
    try {
      const response = await axiosConfig.get('/admin/teachers');
      setOgretmenler(response.data);
    } catch (error) {
      toast.error('Öğretmenler yüklenirken hata oluştu');
    }
  };

  useEffect(() => {
    fetchAvanslar();
    fetchOgretmenler();
  }, []);

  useEffect(() => {
    const fetchMonthlyStats = async () => {
      try {
        const params = new URLSearchParams({
          year: filter.year,
          month: filter.month
        });
        if (filter.ogretmen_id) {
          params.append('ogretmen_id', filter.ogretmen_id);
        }
        
        const response = await axiosConfig.get(`/admin/avans/monthly-total?${params}`);
        setMonthlyStats(response.data);
      } catch (error) {
        console.error('İstatistikler yüklenirken hata:', error);
      }
    };
    
    fetchMonthlyStats();
  }, [filter]);

  // Document'a tıklama dinleyicisi ekle (dropdown'ı kapatmak için)
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Eğer tıklanan element dropdown veya input değilse kapat
      if (!event.target.closest('.relative')) {
        setShowFilterDropdown(false);
        setShowModalDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.ogretmen_id || !formData.tutar) {
      toast.warning('Öğretmen ve tutar alanları zorunludur');
      return;
    }

    const parsedTutar = parseMoneyInput(formData.tutar);
    if (Number.isNaN(parsedTutar) || parsedTutar === 0) {
      toast.warning('Geçerli bir tutar giriniz (0 olamaz)');
      return;
    }

    const payload = { ...formData, tutar: parsedTutar };

    try {
      if (editingAvans) {
        await axiosConfig.put(`/admin/avans/${editingAvans.id}`, payload);
        toast.success('Avans güncellendi');
      } else {
        await axiosConfig.post('/admin/avans', payload);
        toast.success('Avans eklendi');
      }
      
      setShowModal(false);
      resetForm();
      fetchAvanslar();
    } catch (error) {
      toast.error(editingAvans ? 'Güncelleme başarısız' : 'Ekleme başarısız');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Bu avansı silmek istediğinizden emin misiniz?')) {
      try {
        await axiosConfig.delete(`/admin/avans/${id}`);
        toast.success('Avans silindi');
        fetchAvanslar();
      } catch (error) {
        toast.error('Silme işlemi başarısız');
      }
    }
  };

  const handleEdit = (avans) => {
    setEditingAvans(avans);
    setFormData({
      ogretmen_id: avans.ogretmen_id,
      tutar: avans.tutar
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      ogretmen_id: '',
      tutar: ''
    });
    setEditingAvans(null);
    setModalSearchTerm('');
    setShowModalDropdown(false);
  };

  const filteredAvanslar = avanslar.filter(avans => {
    if (filter.ogretmen_id && avans.ogretmen_id !== parseInt(filter.ogretmen_id)) {
      return false;
    }
    
    const avansDate = new Date(avans.created_at);
    if (filter.year && avansDate.getFullYear() !== filter.year) {
      return false;
    }
    if (filter.month && avansDate.getMonth() + 1 !== filter.month) {
      return false;
    }
    
    return true;
  });

  const exportToExcel = () => {
    const headers = ['Öğretmen', 'Tutar', 'Tarih'];
    const data = filteredAvanslar.map(avans => [
      avans.ogretmen_adi,
      avans.tutar,
      new Date(avans.created_at).toLocaleDateString('tr-TR')
    ]);
    
    const csvContent = [headers, ...data]
      .map(row => row.join(','))
      .join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `avanslar_${filter.year}_${filter.month}.csv`;
    link.click();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <FiDollarSign className="text-green-500" />
              Avans Yönetimi
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Öğretmen avanslarını yönetin ve takip edin
            </p>
          </div>
          
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
          >
            <FiPlus />
            Yeni Avans
          </button>
        </div>
      </div>

      {/* İstatistikler - Sadece Süper Admin */}
      {isSuperAdmin && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-200">Toplam Avans</p>
                <p className="text-3xl font-bold mt-2">
                  ₺{Number(monthlyStats.toplam_avans || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              <FiDollarSign className="text-4xl text-purple-300" />
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-pink-500 to-pink-600 rounded-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-pink-200">Avans Sayısı</p>
                <p className="text-3xl font-bold mt-2">{monthlyStats.avans_sayisi || 0}</p>
              </div>
              <FiCalendar className="text-4xl text-pink-300" />
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-200">Ortalama Avans</p>
                <p className="text-3xl font-bold mt-2">
                  ₺{monthlyStats.avans_sayisi > 0
                    ? (Number(monthlyStats.toplam_avans || 0) / Number(monthlyStats.avans_sayisi)).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                    : '0,00'}
                </p>
              </div>
              <FiUser className="text-4xl text-green-300" />
            </div>
          </div>
        </div>
      )}

      {/* Filtreler */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <div className="flex items-center gap-2 mb-4">
          <FiFilter className="text-gray-500" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Filtreler</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Öğretmen Seçimi - Arama Özelliği ile */}
          <div className="relative">
            <div className="relative">
              <input
                type="text"
                value={filterSearchTerm || (
                  filter.ogretmen_id ? 
                  ogretmenler.find(o => o.id === parseInt(filter.ogretmen_id))?.adi || '' : 
                  ''
                )}
                onChange={(e) => {
                  setFilterSearchTerm(e.target.value);
                  setShowFilterDropdown(true);
                  if (!e.target.value) {
                    setFilter({...filter, ogretmen_id: ''});
                  }
                }}
                onFocus={() => setShowFilterDropdown(true)}
                placeholder="Tüm Öğretmenler"
                className="w-full px-4 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-white"
              />
              <FiSearch className="absolute right-3 top-3 text-gray-400" />
            </div>
            
            {/* Dropdown */}
            {showFilterDropdown && (
              <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                <div
                  className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer text-gray-700 dark:text-gray-300"
                  onClick={() => {
                    setFilter({...filter, ogretmen_id: ''});
                    setFilterSearchTerm('');
                    setShowFilterDropdown(false);
                  }}
                >
                  Tüm Öğretmenler
                </div>
                {getFilteredOgretmenler(filterSearchTerm).map(ogretmen => (
                  <div
                    key={ogretmen.id}
                    className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer text-gray-700 dark:text-gray-300"
                    onClick={() => {
                      setFilter({...filter, ogretmen_id: ogretmen.id});
                      setFilterSearchTerm(ogretmen.adi);
                      setShowFilterDropdown(false);
                    }}
                  >
                    {ogretmen.adi}
                  </div>
                ))}
                {getFilteredOgretmenler(filterSearchTerm).length === 0 && (
                  <div className="px-4 py-2 text-gray-500 dark:text-gray-400">
                    Sonuç bulunamadı
                  </div>
                )}
              </div>
            )}
          </div>
          
          <select
            value={filter.month}
            onChange={(e) => setFilter({...filter, month: parseInt(e.target.value)})}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-white"
          >
            {[...Array(12)].map((_, i) => (
              <option key={i} value={i + 1}>
                {new Date(2024, i).toLocaleDateString('tr-TR', { month: 'long' })}
              </option>
            ))}
          </select>
          
          <select
            value={filter.year}
            onChange={(e) => setFilter({...filter, year: parseInt(e.target.value)})}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-white"
          >
            {Array.from({ length: 5 }, (_, index) => new Date().getFullYear() - index).map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
          
          <button
            onClick={exportToExcel}
            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center justify-center gap-2"
          >
            <FiDownload />
            Excel İndir
          </button>
        </div>
      </div>

      {/* Tablo */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          </div>
        ) : filteredAvanslar.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <FiDollarSign className="text-6xl mx-auto mb-4 text-gray-300" />
            <p>Henüz avans kaydı bulunmuyor</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Öğretmen
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Tutar
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Tarih
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    İşlemler
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredAvanslar.map((avans) => (
                  <tr key={avans.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center">
                          <FiUser className="text-purple-600 dark:text-purple-400" />
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {avans.ogretmen_adi}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-semibold text-green-600 dark:text-green-400">
                        ₺{Number(avans.tutar || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {new Date(avans.created_at).toLocaleDateString('tr-TR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEdit(avans)}
                        className="text-purple-600 hover:text-purple-900 dark:text-purple-400 dark:hover:text-purple-300 mr-3"
                      >
                        <FiEdit2 />
                      </button>
                      <button
                        onClick={() => handleDelete(avans.id)}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                      >
                        <FiTrash2 />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black opacity-50" onClick={() => {
              setShowModal(false);
              setModalSearchTerm('');
              setShowModalDropdown(false);
            }}></div>
            
            <div className="relative bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                {editingAvans ? 'Avans Düzenle' : 'Yeni Avans Ekle'}
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Öğretmen
                  </label>
                  <div className="relative">
                    <div className="relative">
                      <input
                        type="text"
                        value={modalSearchTerm || (
                          formData.ogretmen_id ? 
                          ogretmenler.find(o => o.id === parseInt(formData.ogretmen_id))?.adi || '' : 
                          ''
                        )}
                        onChange={(e) => {
                          setModalSearchTerm(e.target.value);
                          setShowModalDropdown(true);
                          if (!e.target.value && !formData.ogretmen_id) {
                            setFormData({...formData, ogretmen_id: ''});
                          }
                        }}
                        onFocus={() => setShowModalDropdown(true)}
                        placeholder="Öğretmen ara ve seç..."
                        className="w-full px-4 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-white"
                        required={!formData.ogretmen_id}
                      />
                      <FiSearch className="absolute right-3 top-3 text-gray-400" />
                    </div>
                    
                    {/* Dropdown */}
                    {showModalDropdown && (
                      <div className="absolute z-20 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {getFilteredOgretmenler(modalSearchTerm).map(ogretmen => (
                          <div
                            key={ogretmen.id}
                            className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer text-gray-700 dark:text-gray-300"
                            onClick={() => {
                              setFormData({...formData, ogretmen_id: ogretmen.id});
                              setModalSearchTerm(ogretmen.adi);
                              setShowModalDropdown(false);
                            }}
                          >
                            {ogretmen.adi}
                          </div>
                        ))}
                        {getFilteredOgretmenler(modalSearchTerm).length === 0 && (
                          <div className="px-4 py-2 text-gray-500 dark:text-gray-400">
                            Öğretmen bulunamadı
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Tutar (₺)
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={formData.tutar}
                    onChange={(e) => setFormData({...formData, tutar: sanitizeMoneyInput(e.target.value)})}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Örn: 250,50"
                    required
                  />
                </div>
                
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                      setModalSearchTerm('');
                      setShowModalDropdown(false);
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:shadow-lg transition-all"
                  >
                    {editingAvans ? 'Güncelle' : 'Ekle'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
